import {useCallback, useContext, useEffect, useState} from 'react';
import {useDebouncedCallback} from 'use-debounce';
import {useDispatch, useSelector} from 'react-redux';
import {MOBILE_WORKSPACE_SIZE_DEFAULT, WORKSPACE_SIZE_DEFAULT} from '../../constants';
import {selectedPresentationSelector, selectedViewSelector} from '../../store/selectors/presentation.selectors';
import {selectedSlideSelector} from '../../store/selectors/slide.selectors';
import {stopWindowResizeSpinnerAction} from '../../store/actions/spinner';
import {DeviceType} from '../../incase-domain/common/DeviceType.enum';
import {SlideRefContext} from '../../context/SlideRefContext';

function usePresScale(): {
    scale: number;
    slideSize: {
        width: number;
        height: number;
    };
    handleResizeDebounced: () => void;
    slideRect: DOMRect | null;
    transformPointFromFraction: (point: {x: number; y: number}) => {x: number; y: number};
    transformPointToFraction: (point: {x: number; y: number}) => {x: number; y: number};
    transformWidthFromPercentage: (length: string) => number;
    transformHeightFromPercentage: (length: string) => number;
    transformWidthToPercentage: (length: number) => string;
    transformHeightToPercentage: (length: number) => string;
} {
    const selectedPresentation = useSelector(selectedPresentationSelector);
    const slide = useSelector(selectedSlideSelector);
    const dispatch = useDispatch();
    const selectedView = useSelector(selectedViewSelector);
    const {slideRef, slidesContainerWrapperRef} = useContext(SlideRefContext);

    // TODO: This seems to work with only slidesContainerWrapperRef; needs checking if slideRef is necessary
    const container = selectedView === DeviceType.MOBILE ? slideRef : slidesContainerWrapperRef;
    const baseContainerSize = selectedView === DeviceType.MOBILE ? MOBILE_WORKSPACE_SIZE_DEFAULT : WORKSPACE_SIZE_DEFAULT;

    const [slideSize, setSlideSize] = useState({width: 0, height: 0});
    const [slideRect, setSlideRect] = useState<DOMRect | null>(null);

    useEffect(() => {
        const handleSlideResize = (): void => {
            const rect = slideRef.current?.getBoundingClientRect() || null;
            setSlideRect(rect);
        };

        handleSlideResize();

        window.addEventListener('resize', handleSlideResize);

        return () => {
            window.removeEventListener('resize', handleSlideResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slideRef.current]);

    const getWorkspaceSize = useCallback(() => {
        if (!slide || !selectedPresentation) return baseContainerSize;

        const currentSlideSize = slide?.workspaceSize;
        const loadedPresentationSize = selectedPresentation.workspaceSize;

        const width = currentSlideSize?.width || loadedPresentationSize?.width || baseContainerSize.width;
        const height = currentSlideSize?.height || loadedPresentationSize?.height || baseContainerSize.height;

        return {width, height};
    }, [selectedPresentation, slide, baseContainerSize]);

    // slide width / height ratio
    const getSlideSizeRatio = useCallback((): number | null => {
        if (!slide || !selectedPresentation) return null;
        const {width, height} = getWorkspaceSize();
        return width / height;
    }, [getWorkspaceSize, selectedPresentation, slide]);

    const handleResize = useCallback((): void => {
        const slideSizeRatio = getSlideSizeRatio();

        if (!slideSizeRatio) return;

        const slideContainer = container.current;
        if (slideContainer) {
            const {width, height} = slideContainer.getBoundingClientRect();
            let newWidth = width;
            let newHeight = height;

            if (slideSizeRatio > 1) {
                // width > heighta
                newHeight = width / slideSizeRatio;

                if (newHeight > height) {
                    newHeight = height;
                    newWidth = height * slideSizeRatio;
                }
            } else if (slideSizeRatio < 1) {
                // height > widtha
                newWidth = height * slideSizeRatio;

                if (newWidth > width) {
                    newWidth = width;
                    newHeight = width / slideSizeRatio;
                }
            }

            // set new slide width height
            setSlideSize({width: newWidth, height: newHeight});
        }
        dispatch(stopWindowResizeSpinnerAction());
    }, [getSlideSizeRatio, container, dispatch]);

    useEffect((): void => {
        handleResize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleResizeDebounced = useDebouncedCallback(() => handleResize(), 100);

    useEffect(() => {
        window.addEventListener('resize', handleResizeDebounced);

        return () => {
            window.removeEventListener('resize', handleResizeDebounced);
        };
    }, [handleResizeDebounced]);

    const transformPointFromFraction = useCallback(
        (point: {x: number; y: number}): {x: number; y: number} => {
            if (!slideRect?.width || !slideRect?.height) return point;
            return {
                x: point.x * slideRect.width,
                y: point.y * slideRect.height,
            };
        },
        [slideRect?.width, slideRect?.height]
    );

    const transformPointToFraction = useCallback(
        (point: {x: number; y: number}): {x: number; y: number} => {
            if (!slideRect?.width || !slideRect.height) return point;
            return {
                x: point.x / slideRect.width,
                y: point.y / slideRect.height,
            };
        },
        [slideRect?.width, slideRect?.height]
    );

    const transformWidthFromPercentage = useCallback(
        (width: string): number => {
            if (!slideRect?.width) return 0;
            const widthNumber = parseFloat(width.replace('%', ''));
            return (widthNumber / 100) * slideRect.width;
        },
        [slideRect?.width]
    );

    const transformHeightFromPercentage = useCallback(
        (height: string): number => {
            if (!slideRect?.height) return 0;
            const heightNumber = parseFloat(height.replace('%', ''));
            return (heightNumber / 100) * slideRect.height;
        },
        [slideRect?.height]
    );

    const transformWidthToPercentage = useCallback(
        (width: number): string => {
            if (!slideRect?.width) return '0%';
            const widthPercentage = (width / slideRect.width) * 100;
            return `${widthPercentage}%`;
        },
        [slideRect?.width]
    );

    const transformHeightToPercentage = useCallback(
        (height: number): string => {
            if (!slideRect?.height) return '0%';
            const heightPercentage = (height / slideRect.height) * 100;
            return `${heightPercentage}%`;
        },
        [slideRect?.height]
    );

    return {
        scale: slideSize.width / getWorkspaceSize().width,
        slideSize,
        handleResizeDebounced,
        slideRect,
        transformPointFromFraction,
        transformPointToFraction,
        transformWidthFromPercentage,
        transformHeightFromPercentage,
        transformWidthToPercentage,
        transformHeightToPercentage,
    };
}

export default usePresScale;
